import { List } from "immutable";

import CheckboxStates from "../../constants/checkboxStates";
import fontSizes from "../../constants/fontSizes";
import globalFontSizes from "../../constants/globalFontSizes";
import { appStateSelectors } from "../../modules/appState";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { mindmapSelectors } from "../../modules/mindmaps";
import { preferencesSelectors } from "../../modules/preferences";
import nativeCommsMessageTypes from "../../nativeComms/nativeCommsMessageTypes";
import { filterReadonlyMenus } from "../../utils/codeSplit";
import {
  // WINDOWS_KEY_LABEL_WIN as keyWin,
  KEY_ARROW_DOWN,
  KEY_ARROW_UP,
  WINDOWS_KEY_LABEL_ALTERNATIVE as keyAlt,
  WINDOWS_KEY_LABEL_CONTROL as keyCtrl,
  WINDOWS_KEY_LABEL_SHIFT as keyShift
} from "./constants";
import readonlyViewMenu from "./readonlyMenuItems/readonlyViewMenus";
import toMenuItemRecord from "./toMenuItemRecord";
import { getShortCutString } from "./utils";

const getMindMapMenu = (state, disabledItems) => {
  const mindMapsList = mindmapSelectors.getMindmapList(state).map(mm => {
    return {
      label: mm.label,
      messageType: nativeCommsMessageTypes.SHOW_MINDMAP,
      value: mm.id
    };
  });

  const createNewMenu = {
    label: "Create New Mindmap",
    messageType: nativeCommsMessageTypes.SHOW_NEW_MINDMAP
  };
  return filterReadonlyMenus(
    readonlyViewMenu,
    mindMapsList.size > 0
      ? mindMapsList
        .update(mindMapsList.size - 1, mm => {
          mm.hasSeparator = true;
          return mm;
        })
        .push(createNewMenu)
      : List([createNewMenu])
  ).map(item => toMenuItemRecord(item, disabledItems));
};

const viewModeMenu = (state, disabledItems) => {
  const mindmapMenu = getMindMapMenu(state, disabledItems);
  const mindmapMenuObj = {
    label: "Mindmap",
    messageType: "mindMapView",
    children: mindmapMenu,
    hasSeparator: true
  };

  return List([
    {
      label: "Timeline",
      messageType: nativeCommsMessageTypes.SHOW_TIMELINE,
      shortcut: getShortCutString([keyCtrl, 1])
    },
    {
      label: "Spreadsheet",
      messageType: nativeCommsMessageTypes.SHOW_SPREADSHEET,
      shortcut: getShortCutString([keyCtrl, 2])
    },
    {
      label: "Relationship",
      messageType: nativeCommsMessageTypes.SHOW_RELATIONSHIPS,
      shortcut: getShortCutString([keyCtrl, 3])
    },
    {
      label: "Subway",
      messageType: nativeCommsMessageTypes.SHOW_SUBWAY,
      shortcut: getShortCutString([keyCtrl, 4])
    },
    {
      label: "Narrative",
      messageType: nativeCommsMessageTypes.SHOW_NARRATIVE,
      shortcut: getShortCutString([keyCtrl, 5])
    },
    {
      label: "Outline",
      messageType: nativeCommsMessageTypes.SHOW_OUTLINE,
      shortcut: getShortCutString([keyCtrl, 6])
    },
    mindmapMenuObj
  ]).map(item => toMenuItemRecord(item, disabledItems));
};

const splitViewMenu = (state, disabledItems) => {
  return List([
    {
      label: "Split Horizontally",
      messageType: nativeCommsMessageTypes.SPLIT_HORIZONTALLY,
      shortcut: `Ctrl+'`,
      disabled: false
    },
    {
      label: "Split Vertically",
      messageType: nativeCommsMessageTypes.SPLIT_VERTICALLY,
      shortcut: `Ctrl+Shift+'`,
      disabled: false,
      hasSeparator: true
    },
    {
      label: "Add Split Top",
      messageType: nativeCommsMessageTypes.ADD_SPLIT_TOP,
      disabled: false
    },
    {
      label: "Add Split Bottom",
      messageType: nativeCommsMessageTypes.ADD_SPLIT_BOTTOM,
      disabled: false
    },
    {
      label: "Add Split Left",
      messageType: nativeCommsMessageTypes.ADD_SPLIT_LEFT,
      disabled: false
    },
    {
      label: "Add Split Right",
      messageType: nativeCommsMessageTypes.ADD_SPLIT_RIGHT,
      disabled: false,
      hasSeparator: true
    },
    {
      label: "Close Split",
      messageType: nativeCommsMessageTypes.CLOSE_SPLIT,
      shortcut: getShortCutString([keyCtrl, keyAlt, "W"]),
      disabled: false
    },
    {
      label: "Close Others",
      messageType: nativeCommsMessageTypes.CLOSE_OTHER_SPLITS,
      shortcut: getShortCutString([keyCtrl, keyShift, keyAlt, "W"]),
      disabled: false
    }
  ]).map(item => toMenuItemRecord(item, disabledItems));
};

const getSidebarType = state => {
  let sidebarTypes = typeSelectors.getSidebarTypes(state);
  //#!if readonly === true
  sidebarTypes = sidebarTypes.filter(t => itemSelectors.getItemIdsOfType(state, t.id).size > 0);
  //#!endif
  return sidebarTypes;
};

const getShowItemListMenu = (state, disabledItems) => {
  const sidebarTypes = getSidebarType(state);
  return sidebarTypes.size > 0
    ? sidebarTypes.map(type => {
      return toMenuItemRecord(
        {
          label: type.label,
          value: type.id,
          messageType: nativeCommsMessageTypes.SHOW_ITEM_LIST
        },
        disabledItems
      );
    })
    : List();
};

const inspectorMenu = (state, disabledItems) => {
  return List([
    {
      label: "Toggle Inspector",
      messageType: nativeCommsMessageTypes.TOGGLE_INSPECTOR,
      shortcut: getShortCutString([keyCtrl, keyAlt, "I"]),
      hasSeparator: true
    },
    {
      label: "Show Range Tab",
      messageType: nativeCommsMessageTypes.SHOW_INSPECTOR_RANGE_TAB,
      shortcut: getShortCutString([keyCtrl, keyAlt, "1"])
    },
    {
      label: "Show Constraints Tab",
      messageType: nativeCommsMessageTypes.SHOW_INSPECTOR_CONSTRAINTS_TAB,
      shortcut: getShortCutString([keyCtrl, keyAlt, "2"])
    },
    {
      label: "Show Relationships Tab",
      messageType: nativeCommsMessageTypes.SHOW_INSPECTOR_RELATIONSHIPS_TAB,
      shortcut: getShortCutString([keyCtrl, keyAlt, "3"])
    },
    {
      label: "Show Properties Tab",
      messageType: nativeCommsMessageTypes.SHOW_INSPECTOR_PROPERTIES_TAB,
      shortcut: getShortCutString([keyCtrl, keyAlt, "4"])
    },
    {
      label: "Show Attachments Tab",
      messageType: nativeCommsMessageTypes.SHOW_INSPECTOR_ATTACHMENTS_TAB,
      shortcut: getShortCutString([keyCtrl, keyAlt, "5"])
    }
  ]).map(item => toMenuItemRecord(item, disabledItems));
};

const filtersMenu = (state, disabledItems) => {
  return List([
    {
      label: "Show Global Filter",
      messageType: nativeCommsMessageTypes.TOGGLE_GLOBAL_FILTER,
      disabled: false,
      shortcut: getShortCutString([keyCtrl, keyShift, "R"])
    },
    {
      label: "Show Local Filter",
      messageType: nativeCommsMessageTypes.TOGGLE_LOCAL_FILTER,
      disabled: false,
      shortcut: getShortCutString([keyCtrl, keyAlt, keyShift, "R"])
    },
    {
      label: "Clear Global Filter",
      messageType: nativeCommsMessageTypes.CLEAR_GLOBAL_FILTER,
      disabled: false,
      shortcut: ""
    },
    {
      label: "Clear Local Filter",
      messageType: nativeCommsMessageTypes.CLEAR_LOCAL_FILTER,
      disabled: false,
      shortcut: ""
    }
  ]).map(item => toMenuItemRecord(item, disabledItems));
};

const textSizeMenu = (state, disabledItems) => {
  const globalFontSize = preferencesSelectors.getGlobalFontSize(state);
  return List([
    {
      label: "Small",
      messageType: nativeCommsMessageTypes.USE_TEXT_SIZE_SMALL,
      disabled: false,
      state: globalFontSize === globalFontSizes.Small ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED
    },
    {
      label: "Medium",
      messageType: nativeCommsMessageTypes.USE_TEXT_SIZE_MEDIUM,
      disabled: false,
      state: globalFontSize === globalFontSizes.Medium ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED
    },
    {
      label: "Large",
      messageType: nativeCommsMessageTypes.USE_TEXT_SIZE_LARGE,
      disabled: false,
      state: globalFontSize === globalFontSizes.Large ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED
    },
    {
      label: "Huge",
      messageType: nativeCommsMessageTypes.USE_TEXT_SIZE_XLARGE,
      disabled: false,
      state: globalFontSize === globalFontSizes.XLarge ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED
    },
    {
      label: "Jumbo",
      messageType: nativeCommsMessageTypes.USE_TEXT_SIZE_XXLARGE,
      disabled: false,
      state: globalFontSize === globalFontSizes.XXLarge ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED
    }
  ]).map(item => toMenuItemRecord(item, disabledItems));
};

const workViewZoomMenu = (state, disabledItems) => {
  const fontPercentage = appStateSelectors.getFontPercentage(state);

  return List(fontSizes.availablePercentages)
    .map(percentage => {
      return {
        label: `${percentage}%`,
        value: percentage.toString(),
        messageType: nativeCommsMessageTypes.SET_FONT_PERCENTAGE,
        disabled: false,
        state: fontPercentage === percentage ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED
      };
    })
    .map(item => toMenuItemRecord(item, disabledItems));
};

export default (state, disabledItems) => {
  const showItemListMenu = getShowItemListMenu(state, disabledItems);

  const showItemListMenuObj =
    showItemListMenu.size > 0
      ? {
        label: "Show Item List",
        messageType: "showItemList",
        children: showItemListMenu
      }
      : null;

  return filterReadonlyMenus(
    readonlyViewMenu,
    List([
      {
        label: "View Mode",
        messageType: "viewMode",
        children: viewModeMenu(state, disabledItems)
      },
      {
        label: "Split View",
        messageType: "splitView",
        children: splitViewMenu(state, disabledItems),
        hasSeparator: true
      },
      {
        label: "Expand Parent Item",
        messageType: nativeCommsMessageTypes.EXPAND_PARENT_ITEM,
        disabled: false,
        shortcut: getShortCutString([keyCtrl, keyShift, KEY_ARROW_DOWN])
      },
      {
        label: "Collapse Parent Item",
        messageType: nativeCommsMessageTypes.COLLAPSE_PARENT_ITEM,
        disabled: false,
        shortcut: getShortCutString([keyCtrl, keyShift, KEY_ARROW_UP]),
        hasSeparator: true
      },
      showItemListMenuObj,
      {
        label: "Show Calendar Markers",
        messageType: nativeCommsMessageTypes.SHOW_CALENDAR_MARKERS,
        shortcut: getShortCutString([keyCtrl, keyShift, "M"]),
        hasSeparator: true
      },
      {
        label: "Inspector",
        messageType: "inspector",
        children: inspectorMenu(state, disabledItems)
      },
      {
        label: "Filters",
        messageType: "filters",
        children: filtersMenu(state, disabledItems)
      },
      {
        label: "Show Settings",
        messageType: nativeCommsMessageTypes.SHOW_SETTINGS,
        shortcut: getShortCutString([keyCtrl, ","]),
        hasSeparator: true
      },
      {
        label: "Text size",
        messageType: "textSize",
        children: textSizeMenu(state, disabledItems)
      },
      {
        label: "Workview Zoom",
        messageType: "workViewZoom",
        children: workViewZoomMenu(state, disabledItems),
        hasSeparator: true
      }
    ]).filter(m => m)
  ).map(item => toMenuItemRecord(item, disabledItems));
};
